/**
 * Hook personalizado para obtener el contador de interacciones del usuario.
 * Lee directamente de la tabla interaction_counter con TanStack Query para caché automático.
 * Retorna visitas, me_gustas y mensajes.
 * 
 * @param {Object} session - Sesión de autenticación del usuario
 * @returns {Object} - Resultado de la query con contadores de interacciones
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export const useInteractionCounter = (session) => {
  const userId = session?.user?.id;

  return useQuery({
    /**
     * Query key única por usuario
     */
    queryKey: ['interactionCounter', userId],
    
    /**
     * Solo ejecutar si hay usuario
     */
    enabled: !!userId,
    
    /**
     * Función para obtener contadores de interacciones
     */
    queryFn: async () => {
      if (!userId) {
        throw new Error('Usuario no disponible');
      }

      try {
        console.log('[useInteractionCounter] Obteniendo contadores para:', userId);

        const { data, error } = await supabase
          .from('interaction_counter')
          .select('visitas, me_gustas, mensajes')
          .eq('user_id', userId)
          .single();

        if (error) {
          // Si no hay registro, crear uno con valores iniciales en 0
          if (error.code === 'PGRST116') {
            console.log('[useInteractionCounter] No hay registro de contador, creando uno nuevo');
            
            // Crear el registro inicial para el usuario
            const { error: insertError } = await supabase
              .from('interaction_counter')
              .insert({
                user_id: userId,
                visitas: 0,
                me_gustas: 0,
                mensajes: 0,
                updated_at: new Date().toISOString()
              });

            if (insertError) {
              console.error('[useInteractionCounter] Error creando registro inicial:', insertError);
              throw insertError;
            }

            console.log('[useInteractionCounter] Registro inicial creado exitosamente');
            return { visitas: 0, me_gustas: 0, mensajes: 0 };
          }
          throw error;
        }

        const result = {
          visitas: data?.visitas || 0,
          me_gustas: data?.me_gustas || 0,
          mensajes: data?.mensajes || 0
        };

        console.log('[useInteractionCounter] Contadores obtenidos:', result);
        return result;
      } catch (err) {
        console.error('[useInteractionCounter] Error obteniendo contadores:', err);
        throw err;
      }
    },
    
    /**
     * Select para estabilizar el objeto de datos
     * Evita re-renders cuando los valores son iguales
     */
    select: (data) => {
      return {
        visitas: data?.visitas || 0,
        me_gustas: data?.me_gustas || 0,
        mensajes: data?.mensajes || 0
      };
    },
    
    /**
     * Tiempo de caché: 30 segundos
     * Aumentado para reducir re-renders innecesarios
     * Los cambios en tiempo real se manejan via invalidación explícita
     */
    staleTime: 30 * 1000,
    
    /**
     * No refrescar automáticamente
     * Dependeremos de Supabase Realtime para actualizaciones
     */
    refetchInterval: false,
    
    /**
     * Estrategia de reintentos
     */
    retry: (failureCount, error) => {
      // No reintentar para errores de JWT expired
      if (error?.code === 'PGRST301') {
        return false;
      }
      return failureCount < 3;
    },
    
    /**
     * Callback cuando hay error en la query
     */
    onError: (error) => {
      if (error?.code !== 'PGRST301') {
        console.error('[useInteractionCounter] Error en query:', error);
      }
    },
  });
};
